import { DiscountCode } from "../types";
import { toMoney, formatToCurrency } from "shared/utils";
import moment from "moment";
import { DiscountType } from "shared/types";

// Convert form input value to discount amount based on discount type
export const convertToDiscountAmount = (
  value: string | number,
  discountType: DiscountType
): number => {
  if (discountType === "flat")
    // Convert dollars to cents
    return Math.round(Number(value) * 100);

  if (discountType === "percentage")
    // Keep percentage as-is
    return Number(value);

  // Free is always 100%
  return 100;
};

// Format discount amount for display (e.g. "$5.00 flat", "10%", "Free")
export const formatDiscountValue = (discount: DiscountCode): string => {
  if (discount.discount_type === "flat")
    return `$${((discount.discount_amount || 0) / 100).toFixed(2)} flat`;

  if (discount.discount_type === "percentage")
    return `${discount.discount_amount || 0}%`;

  return "Free";
};

// no "percent" suffix
export const formatDiscountType = (discount: DiscountCode): string | null => {
  switch (discount.discount_type) {
    case "flat":
      return discount.discount_amount !== null
        ? toMoney(discount.discount_amount / 100)
        : null;
    case "percentage":
      return discount.discount_amount !== null
        ? `${discount.discount_amount}%`
        : null;
    case "free":
      return "100%";
    default:
      return null;
  }
};

export const formatDiscountDateRange = (discount: DiscountCode): string => {
  const startDate = moment(discount.start_date).format("MMM D, YYYY");
  if (!discount.end_date) return `${startDate} - No end date`;
  const endDate = moment(discount.end_date).format("MMM D, YYYY");
  return `${startDate} - ${endDate}`;
};

// Format discount amount for tooltip display using formatToCurrency
export const formatDiscountAmountForTooltip = (
  discount: { discount_type?: string; discount_amount?: number | null } | null,
  showPercentageAsAmount?: boolean
): string => {
  if (!discount) return "";

  const amount = discount.discount_amount || 0;
  if (discount.discount_type === "percentage" && !showPercentageAsAmount)
    return `${amount}%`;
  if (discount.discount_type === "free") return "100%";

  return formatToCurrency(
    { cents: amount, currency: "USD" },
    { minimumFractionDigits: 2 }
  );
};

// Get raw discount value for form inputs (returns editable values)
export const getDiscountValue = (editDiscount: DiscountCode | null): string => {
  if (!editDiscount || editDiscount.discount_amount === null) return "";

  if (editDiscount.discount_type === "flat")
    return String(editDiscount.discount_amount / 100);

  if (editDiscount.discount_type === "free") return "100";

  return String(editDiscount.discount_amount);
};
